import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import {
  Grid,
  MenuItem,
  Portal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AnalyticsSpacing, PageSubAction } from "consts";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useState } from "react";
import {
  CHART_KEY_CONTENT_INSIGHTS_LANDING_TABLE,
  CHART_KEY_CONTENT_INSIGHTS_TOP_LANDING_PIE,
  CHART_KEY_CONTENT_INSIGHTS_TOP_LANDING_TREND,
} from "features/analytics/constants/chartKeys";
import { table10Rows } from "features/analytics";

function ContentInsights() {
  const metricToName = useMetricName();
  const dimensionToName = useDimensionName();
  const [dimension, setDimension] = useState<ApiDimension>(
    ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
  );
  const [metric, setMetric] = useState<ApiMetric>(
    ApiMetric.METRIC_SESSIONS_COUNT,
  );

  const dimensions = [
    ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
    ApiDimension.DIMENSION_LANDING_PAGE_PATH,
  ];

  return (
    <>
      <Portal container={() => document.getElementById(PageSubAction)}>
        <Stack direction={"row"} spacing={2} alignItems="center">
          <Typography noWrap variant="body1" fontWeight={700}>
            View results by
          </Typography>
          <TextField
            select
            size="small"
            value={dimension}
            sx={{ minWidth: 200 }}
            onChange={(e) => setDimension(e.target.value as ApiDimension)}
          >
            {dimensions.map((d) => (
              <MenuItem key={d} value={d}>
                {dimensionToName(d)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Portal>

      <Grid
        container
        spacing={AnalyticsSpacing}
        mb={4}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: AnalyticsSpacing,
        }}
      >
        <Grid container spacing={AnalyticsSpacing}>
          <Grid item xs={8} sx={{ minHeight: 450 }}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_CONTENT_INSIGHTS_TOP_LANDING_TREND}
              chart={{
                name: "Top 5 Landing Locations by",
                dimension: [
                  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                  dimension,
                ],
                template: ApiChartTemplate.TREND,
                type: ApiChartType.GRAPH,
                limit: 5,
                attributes: [],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                  },
                ],
                displayOptions: {
                  availableMetrics: [
                    ApiMetric.METRIC_SESSIONS_COUNT,
                    ApiMetric.METRIC_SESSION_SALES_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                  ],
                },
              }}
              onChangeMetric={setMetric}
            />
          </Grid>
          <Grid item xs={4}>
            <StandardAnalyticsCard
              chartKey={CHART_KEY_CONTENT_INSIGHTS_TOP_LANDING_PIE}
              chart={{
                name: `Top 15 ${dimensionToName(dimension)} by ${metricToName(
                  metric,
                )}`,
                dimension: [dimension],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.PIE,
                limit: 15,
                attributes: [],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [metric],
                  },
                ],
                displayOptions: {
                  showTotals: true,
                },
              }}
            />
          </Grid>
        </Grid>

        <Grid sx={{ flexGrow: 1, minHeight: table10Rows }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_CONTENT_INSIGHTS_LANDING_TABLE}
            chart={{
              name: "",
              dimension: [dimension],
              template: ApiChartTemplate.TABLE,
              type: ApiChartType.TABLE,
              displayOptions: {
                rowsPerPage: 15,
              },
              attributes: [],
              orderBy: `${ApiMetric.METRIC_SESSIONS_COUNT} desc`,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [
                    ApiMetric.METRIC_SESSIONS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_CHECKOUT_START_RATE,
                    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                    ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                    ApiMetric.METRIC_REVENUE,
                  ],
                },
              ],
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default ContentInsights;
